// src/components/forms/InspectionForm.tsx — schedule an inspection, then complete it.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import { usePropertyOptions, useTenancyOptions, useUnitOptions, useUserOptions } from '../../lib/options';
import { Button, Field, Input, Modal, Select, Textarea } from '../ui';
import { FormError, FormGrid, nulled } from './common';

type I = Record<string, any>;
type Item = { key: string; label: string; result?: string | null; note?: string | null };
type Finding = { title: string; description: string; severity: string; raiseWorkOrder: boolean };

const TYPES = [
  ['MOVE_IN', 'Move in'], ['MOVE_OUT', 'Move out'], ['PERIODIC', 'Periodic'],
  ['SNAGGING', 'Snagging'], ['FIRE_DOOR', 'Fire door'], ['HANDOVER', 'Handover'],
];

/** What each kind of inspection actually checks, so nobody starts from a blank list. */
const TEMPLATES: Record<string, string[]> = {
  MOVE_IN: ['Meter readings taken', 'Keys handed over', 'Cleanliness', 'Walls and ceilings', 'Flooring', 'Kitchen and appliances', 'Bathroom', 'Windows and doors', 'Smoke and CO alarms tested'],
  MOVE_OUT: ['Meter readings taken', 'Keys returned', 'Cleanliness', 'Walls and ceilings', 'Flooring', 'Kitchen and appliances', 'Bathroom', 'Damage beyond fair wear and tear', 'Belongings removed'],
  PERIODIC: ['Damp or mould', 'Smoke and CO alarms', 'Heating and hot water', 'Signs of damage', 'Communal areas', 'Unauthorised occupants or pets'],
  SNAGGING: ['Finishes and decoration', 'Doors and handles', 'Windows and seals', 'Sanitaryware', 'Kitchen units', 'Electrical fittings', 'Heating commissioned'],
  FIRE_DOOR: ['Self-closer operates', 'Intumescent strips intact', 'Gaps within tolerance', 'Hinges secure', 'Signage present', 'Glazing intact'],
  HANDOVER: ['Documentation received', 'Warranties recorded', 'Meters and serials logged', 'Keys and fobs counted', 'Snags signed off'],
};
const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 100);
const itemsFor = (type: string): Item[] => (TEMPLATES[type] ?? []).map((label) => ({ key: slug(label), label }));

export function InspectionForm({ open, onClose, inspection, preset }: { open: boolean; onClose: () => void; inspection?: I;
  /** A new inspection with these already chosen (the property page's "Record a visit"). */ preset?: { propertyId?: string; unitId?: string | null; inspectionType?: string } }) {
  const isNew = !inspection;
  const qc = useQueryClient();
  const properties = usePropertyOptions();
  const users = useUserOptions();
  const [form, setForm] = useState(() => ({
    propertyId: inspection?.propertyId ?? preset?.propertyId ?? '', unitId: inspection?.unitId ?? preset?.unitId ?? '', tenancyId: inspection?.tenancyId ?? '',
    inspectionType: inspection?.inspectionType ?? preset?.inspectionType ?? 'PERIODIC',
    scheduledFor: inspection?.scheduledFor ?? new Date().toISOString().slice(0, 10),
    inspectorId: inspection?.inspectorId ?? '', status: inspection?.status ?? 'SCHEDULED',
  }));
  const [checklist, setChecklist] = useState<Item[]>(() => (inspection?.checklist?.length ? inspection.checklist : itemsFor(form.inspectionType)));
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const units = useUnitOptions({ propertyId: form.propertyId }, !!form.propertyId);
  const tenancies = useTenancyOptions(form.unitId);

  const chooseType = (t: string) => { set({ inspectionType: t }); if (isNew) setChecklist(itemsFor(t)); };

  const body = () => ({
    ...(isNew ? { propertyId: form.propertyId } : {}),
    unitId: nulled(form.unitId), tenancyId: nulled(form.tenancyId),
    inspectionType: form.inspectionType, scheduledFor: nulled(form.scheduledFor), inspectorId: nulled(form.inspectorId),
    checklist: checklist.filter((i) => i.label.trim()).map((i) => ({ key: i.key || slug(i.label), label: i.label.trim() })),
    ...(isNew ? {} : { status: form.status }),
  });

  const save = useMutation({
    mutationFn: async () => (isNew ? api.post<I>('/inspections', body()) : api.patch<I>(`/inspections/${inspection!.id}`, body())),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['/inspections'] }); onClose(); },
  });

  return (
    <Modal open={open} onClose={onClose} size="lg" title={isNew ? 'Schedule an inspection' : 'Inspection'}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => save.mutate()} disabled={!form.propertyId || save.isPending}>{save.isPending ? 'Saving…' : isNew ? 'Schedule' : 'Save changes'}</Button></>}>
      <div className="space-y-4">
        <FormGrid>
          <Field label="Scheme"><Select value={form.propertyId} disabled={!isNew} onChange={(e) => set({ propertyId: e.target.value, unitId: '', tenancyId: '' })} className="w-full"><option value="">Choose a scheme…</option>{(properties.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</Select></Field>
          <Field label="Unit"><Select value={form.unitId} onChange={(e) => set({ unitId: e.target.value, tenancyId: '' })} className="w-full" disabled={!form.propertyId}><option value="">Communal or whole scheme</option>{(units.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.reference}</option>)}</Select></Field>

          <Field label="Type"><Select value={form.inspectionType} onChange={(e) => chooseType(e.target.value)} className="w-full">{TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></Field>
          <Field label="Scheduled for"><Input type="date" value={form.scheduledFor} onChange={(e) => set({ scheduledFor: e.target.value })} className="w-full" /></Field>

          <Field label="Inspector"><Select value={form.inspectorId} onChange={(e) => set({ inspectorId: e.target.value })} className="w-full"><option value="">Unassigned</option>{(users.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}</Select></Field>
          <Field label="Tenancy" hint="Links a move-in or move-out to the right let"><Select value={form.tenancyId} onChange={(e) => set({ tenancyId: e.target.value })} className="w-full" disabled={!form.unitId}><option value="">Not linked</option>{(tenancies.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.reference} — {t.status.toLowerCase()}</option>)}</Select></Field>

          {!isNew && <Field label="Status"><Select value={form.status} onChange={(e) => set({ status: e.target.value })} className="w-full">{['SCHEDULED', 'IN_PROGRESS', 'CANCELLED'].map((s) => <option key={s} value={s}>{s[0] + s.slice(1).toLowerCase()}</option>)}</Select></Field>}
        </FormGrid>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[12px] font-medium text-ink-600">Checklist <span className="text-ink-400">· {checklist.length} items</span></span>
            <button type="button" onClick={() => setChecklist((c) => [...c, { key: '', label: '' }])} className="inline-flex items-center gap-1 text-[12px] font-medium text-bronze-600 hover:underline"><Plus className="h-3.5 w-3.5" /> Add an item</button>
          </div>
          <div className="space-y-1.5">
            {checklist.map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input value={item.label} onChange={(e) => setChecklist((c) => c.map((x, j) => j === i ? { ...x, label: e.target.value, key: x.key || slug(e.target.value) } : x))} className="flex-1 !py-1.5 !text-[12.5px]" placeholder="What is being checked" />
                <button type="button" onClick={() => setChecklist((c) => c.filter((_, j) => j !== i))} className="rounded-full p-1.5 text-ink-300 transition hover:bg-red-50 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </div>

        <FormError error={save.error} />
      </div>
    </Modal>
  );
}

/**
 * Completing an inspection is where it earns its keep: anything ticked "raise a job"
 * becomes a work order in the same transaction, so a failed fire door cannot be
 * written down and then forgotten.
 */
export function CompleteInspection({ open, onClose, inspection }: { open: boolean; onClose: () => void; inspection: I }) {
  const qc = useQueryClient();
  const [checklist, setChecklist] = useState<Item[]>(() => (inspection.checklist ?? []).map((i: Item) => ({ ...i, result: i.result ?? null, note: i.note ?? '' })));
  const [findings, setFindings] = useState<Finding[]>(() => (inspection.findings ?? []).map((f: Record<string, any>) => ({
    title: f.title ?? '', description: f.description ?? '', severity: f.severity ?? 'MEDIUM', raiseWorkOrder: !!f.raiseWorkOrder,
  })));
  const [priority, setPriority] = useState('ROUTINE');

  const run = useMutation({
    mutationFn: async () => api.post(`/inspections/${inspection.id}/complete`, {
      checklist: checklist.map((i) => ({ key: i.key, label: i.label, result: i.result ?? null, note: i.note || null })),
      findings: findings.filter((f) => f.title.trim().length >= 3).map((f) => ({
        title: f.title.trim(), description: f.description.trim() || null, severity: f.severity, raiseWorkOrder: f.raiseWorkOrder,
      })),
      workOrderPriority: priority,
    }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['/inspections'] }); void qc.invalidateQueries({ queryKey: ['/work-orders'] }); onClose(); },
  });

  const fails = checklist.filter((i) => i.result === 'FAIL').length;
  const jobs = findings.filter((f) => f.raiseWorkOrder && f.title.trim().length >= 3).length;

  return (
    <Modal open={open} onClose={onClose} size="lg" title="Complete this inspection"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => run.mutate()} disabled={run.isPending}>{run.isPending ? 'Completing…' : jobs > 0 ? `Complete and raise ${jobs} job${jobs === 1 ? '' : 's'}` : 'Complete'}</Button></>}>
      <div className="space-y-4">
        <div>
          <p className="mb-2 text-[12px] font-medium text-ink-600">Checklist</p>
          <div className="space-y-1.5">
            {checklist.map((item, i) => (
              <div key={item.key || i} className="flex flex-wrap items-center gap-2 rounded-xl border border-ink-200 px-3 py-2">
                <span className="flex-1 text-[12.5px] text-ink-700">{item.label}</span>
                <div className="flex gap-1">
                  {['PASS', 'FAIL', 'NA'].map((r) => (
                    <button key={r} type="button" onClick={() => setChecklist((c) => c.map((x, j) => j === i ? { ...x, result: x.result === r ? null : r } : x))}
                      className={`h-7 rounded-lg border px-2.5 text-[12px] transition ${item.result === r ? (r === 'FAIL' ? 'border-red-500 bg-red-500 text-on-primary' : r === 'PASS' ? 'border-emerald-600 bg-emerald-600 text-on-primary' : 'border-bronze-500 bg-bronze-50 text-bronze-700') : 'border-ink-200 bg-white text-ink-500 hover:border-ink-400'}`}>
                      {r === 'NA' ? 'n/a' : r[0] + r.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
                {item.result === 'FAIL' && <Input value={item.note ?? ''} onChange={(e) => setChecklist((c) => c.map((x, j) => j === i ? { ...x, note: e.target.value } : x))} placeholder="What is wrong" className="w-full !py-1.5 !text-[12px]" />}
              </div>
            ))}
            {!checklist.length && <p className="rounded-xl border border-ink-200 py-4 text-center text-[12px] text-ink-400">No checklist on this inspection</p>}
          </div>
          {fails > 0 && <p className="mt-2 text-[12px] text-amber-700">{fails} item{fails === 1 ? '' : 's'} failed. Add them as findings below so they turn into work.</p>}
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[12px] font-medium text-ink-600">Findings</span>
            <button type="button" onClick={() => setFindings((f) => [...f, { title: '', description: '', severity: 'MEDIUM', raiseWorkOrder: true }])} className="inline-flex items-center gap-1 text-[12px] font-medium text-bronze-600 hover:underline"><Plus className="h-3.5 w-3.5" /> Add a finding</button>
          </div>
          <div className="space-y-2">
            {findings.map((f, i) => (
              <div key={i} className="space-y-2 rounded-xl border border-ink-200 p-3">
                <div className="flex items-center gap-2">
                  <Input value={f.title} onChange={(e) => setFindings((x) => x.map((y, j) => j === i ? { ...y, title: e.target.value } : y))} placeholder="Cracked pane in the hall window" className="flex-1 !py-1.5 !text-[12.5px]" />
                  <Select value={f.severity} onChange={(e) => setFindings((x) => x.map((y, j) => j === i ? { ...y, severity: e.target.value } : y))} className="w-28 !py-1.5 !text-[12px]">{['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map((s) => <option key={s} value={s}>{s[0] + s.slice(1).toLowerCase()}</option>)}</Select>
                  <button type="button" onClick={() => setFindings((x) => x.filter((_, j) => j !== i))} className="rounded-full p-1.5 text-ink-300 transition hover:bg-red-50 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                <Textarea rows={2} value={f.description} onChange={(e) => setFindings((x) => x.map((y, j) => j === i ? { ...y, description: e.target.value } : y))} placeholder="Detail" className="w-full !py-1.5 !text-[12px]" />
                <label className="flex items-center gap-2 text-[12px] text-ink-600"><input type="checkbox" checked={f.raiseWorkOrder} onChange={(e) => setFindings((x) => x.map((y, j) => j === i ? { ...y, raiseWorkOrder: e.target.checked } : y))} className="h-3.5 w-3.5 accent-ink-900" /> Raise a work order for this</label>
              </div>
            ))}
            {!findings.length && <p className="rounded-xl border border-ink-200 py-4 text-center text-[12px] text-ink-400">Nothing found — a clean inspection</p>}
          </div>
        </div>

        {jobs > 0 && (
          <FormGrid>
            <Field label="Priority for the jobs raised"><Select value={priority} onChange={(e) => setPriority(e.target.value)} className="w-full">{['EMERGENCY', 'URGENT', 'ROUTINE', 'PLANNED'].map((p) => <option key={p} value={p}>{p[0] + p.slice(1).toLowerCase()}</option>)}</Select></Field>
          </FormGrid>
        )}

        <FormError error={run.error} />
      </div>
    </Modal>
  );
}
